<template>
  <Modal
    ref="modalRef"
    :title="modalTitle"
    :confirm-enabled="isValid"
    confirm-text="Add"
    cancel-text="Cancel"
    @confirm="onConfirm"
    @cancel="onCancel"
    @close="onCancel"
  >
    <div class="add-property-form">
      <FormField
        v-if="!context.isArray"
        label="Property Name"
        description="Unique key for the new property"
        :required="true"
      >
        <InputBox
          ref="inputRef"
          v-model="propertyName"
          placeholder="e.g. newProperty"
          @keydown.enter.prevent="onConfirm"
        />
      </FormField>

      <FormField
        label="Type"
        description="Select the value type"
        :required="true"
      >
        <SelectBox
          v-model="valueType"
          :options="typeOptions"
          placeholder="Select type"
        />
      </FormField>

      <FormField
        v-if="showInitialValueInput"
        label="Initial Value"
        :description="`Enter initial value for a ${valueType}`"
      >
        <InputBox v-if="valueType === 'string'" v-model="stringValue" placeholder="Enter text" />
        <InputBox v-else-if="valueType === 'number' || valueType === 'integer'" v-model.number="numberValue" type="number" :step="(valueType === 'integer' ? 1 : undefined) as any" />
        <Toggle v-else-if="valueType === 'boolean'" v-model="booleanValue" label="Enabled" />
        <InputBox v-else-if="valueType === 'array'" disabled model-value="[]" />
        <InputBox v-else-if="valueType === 'object'" disabled model-value="{}" />
        <div v-else-if="valueType === 'secret'" class="secret-fields">
          <InputBox v-model="secretRef" placeholder="e.g. database/connection-string" />
          <SelectBox
            v-if="hasConfiguredVaults"
            v-model="secretVault"
            :options="vaultOptions"
            placeholder="Select vault alias"
          />
          <InputBox v-else v-model="secretVault" placeholder="Vault alias, e.g. default" />
          <InputBox v-model="secretVersion" placeholder="Optional version" />
        </div>
        <div v-else-if="valueType === 'vault'" class="vault-fields">
          <SelectBox
            v-model="vaultProvider"
            :options="providerOptions"
            placeholder="Select provider"
          />
          <InputBox v-model="vaultUri" type="url" placeholder="https://example.vault.azure.net/" />
        </div>
      </FormField>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Modal } from '@/components/atoms'
import InputBox from '@/components/atoms/InputBox.vue'
import Toggle from '@/components/atoms/Toggle.vue'
import SelectBox from '@/components/atoms/SelectBox.vue'
import { FormField } from '@/components/molecules'
import { buildSecretReference } from '@/utils/secret-reference'

interface Props { vaultAliases?: string[] }
interface Context { parentPath: string; existingKeys: string[]; isArray: boolean }
interface Emits { (e:'add', payload:{ key?: string; value:any; type:string; parentPath:string }):void; (e:'cancel'):void }
const props = withDefaults(defineProps<Props>(), { vaultAliases: () => [] })
const emit = defineEmits<Emits>()

const modalRef = ref<InstanceType<typeof Modal>|null>(null)
const propertyName = ref('')
const valueType = ref<string>('string')
const stringValue = ref('')
const numberValue = ref<number>(0)
const booleanValue = ref(false)
const secretRef = ref('')
const secretVault = ref('default')
const secretVersion = ref('')
const vaultProvider = ref('azure-keyvault')
const vaultUri = ref('')
const context = ref<Context>({ parentPath:'', existingKeys:[], isArray:false })

const typeOptions = [
  { value:'string', label:'String' },
  { value:'number', label:'Number' },
  // { value:'integer', label:'Integer' },
  { value:'boolean', label:'Boolean' },
  { value:'object', label:'Object' },
  { value:'array', label:'Array' },
  { value:'vault', label:'Vault' },
  { value:'secret', label:'Secret Reference' }
]

const showInitialValueInput = computed(()=> ['string','number','integer','boolean','vault','secret'].includes(valueType.value))
const hasConfiguredVaults = computed(() => props.vaultAliases.length > 0)
const defaultVaultAlias = computed(() => props.vaultAliases.find(alias => alias === 'default') || props.vaultAliases[0] || 'default')
const providerOptions = [
  { value:'azure-keyvault', label:'Azure Key Vault' },
]
const vaultOptions = computed(() => props.vaultAliases.map(alias => ({
  value: alias,
  label: alias === 'default' ? 'default (recommended)' : alias,
})))

const modalTitle = computed(() => {
  if (!context.value.parentPath) return 'Add Root Property'
  
  // Extract the last segment of the path and capitalize it
  // Remove leading slash and split by '/'
  const cleanPath = context.value.parentPath.replace(/^\//, '')
  const pathSegments = cleanPath.split('/')
  const parentName = pathSegments[pathSegments.length - 1]
  const capitalizedName = parentName.charAt(0).toUpperCase() + parentName.slice(1)
  
  if (context.value.isArray) {
    return `Add ${capitalizedName} Array Item`
  }
  
  return `Add ${capitalizedName} Property`
})

const isValid = computed(()=> {
  if(!valueType.value) return false
  if(context.value.isArray) return true
  const name = propertyName.value.trim()
  const hasValidName = name.length>0 && /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !context.value.existingKeys.includes(name)
  if (!hasValidName) return false
  if (valueType.value === 'secret') {
    return secretRef.value.trim().length > 0
  }
  if (valueType.value === 'vault') {
    return vaultUri.value.trim().length > 0
  }
  return true
})

function initialValue(){
  switch(valueType.value){
    case 'string': return stringValue.value
    case 'number': return numberValue.value
    case 'integer': return Math.trunc(numberValue.value)
    case 'boolean': return booleanValue.value
    case 'object': return {}
    case 'array': return []
    case 'vault': return { provider: vaultProvider.value, uri: vaultUri.value.trim() }
    case 'secret': return buildSecretReference({ ref: secretRef.value, vault: secretVault.value, version: secretVersion.value })
    default: return stringValue.value
  }
}

function open(parentPath:string, existingKeys:string[], isArray:boolean){
  context.value = { parentPath, existingKeys, isArray }
  propertyName.value=''
  valueType.value='string'
  stringValue.value=''
  numberValue.value=0
  booleanValue.value=false
  secretRef.value=''
  secretVault.value=defaultVaultAlias.value
  secretVersion.value=''
  vaultProvider.value='azure-keyvault'
  vaultUri.value=''
  modalRef.value?.showModal()
}

function onConfirm(){
  if(!isValid.value) return
  emit('add', { key: context.value.isArray? undefined: propertyName.value.trim(), value: initialValue(), type: valueType.value, parentPath: context.value.parentPath })
  modalRef.value?.closeModal()
}
function onCancel(){ emit('cancel'); modalRef.value?.closeModal() }

defineExpose({ open })
</script>

<style scoped lang="scss">
.add-property-form { display:flex; flex-direction:column; gap:16px; min-width:380px; }

.secret-fields {
  display: grid;
  gap: 10px;
}

.vault-fields {
  display: grid;
  gap: 10px;
}
</style>
